import React, { useEffect, useState } from 'react';
import Textarea from '../../../components/ui/Textarea';
import {
  deleteVisualAsset,
  getVisualAssetPreview,
  uploadVisualAsset,
} from '../../../api/visualAssets';
import { getApiErrorMessage } from '../../../utils/errors';

function VisualAssetUploadField({
  label,
  hint,
  assetType,
  agentId,
  asset,
  onAssetChange,
  disabled = false,
}) {
  const [previewUrl, setPreviewUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      if (!agentId || !asset?.s3_key) {
        setPreviewUrl('');
        return;
      }

      try {
        const response = await getVisualAssetPreview(agentId, assetType);
        if (!cancelled) {
          setPreviewUrl(response.preview_url || '');
        }
      } catch {
        if (!cancelled) {
          setPreviewUrl('');
        }
      }
    }

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [agentId, assetType, asset?.s3_key]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !agentId) {
      return;
    }

    setError('');
    setIsUploading(true);
    try {
      const response = await uploadVisualAsset(agentId, assetType, file);
      onAssetChange(response.asset);
      setPreviewUrl(response.preview_url || '');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível enviar o arquivo.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!agentId || !asset) {
      return;
    }

    setError('');
    setIsRemoving(true);
    try {
      await deleteVisualAsset(agentId, assetType);
      onAssetChange(null);
      setPreviewUrl('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível remover o arquivo.'));
    } finally {
      setIsRemoving(false);
    }
  };

  const busy = disabled || isUploading || isRemoving;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-content-secondary">{label}</p>
      {hint && <p className="text-xs text-content-muted -mt-1">{hint}</p>}

      {!agentId && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Salve o agente primeiro para enviar {assetType === 'logo' ? 'a logo' : 'a referência'}.
        </p>
      )}

      {previewUrl && (
        <div className="rounded-lg border border-surface-border bg-white p-2 inline-block">
          <img
            src={previewUrl}
            alt={label}
            className="max-h-24 max-w-full object-contain"
          />
        </div>
      )}

      {asset?.file_name && (
        <p className="text-xs text-content-muted">Arquivo: {asset.file_name}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <label className={`btn-secondary text-sm px-3 py-2 cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''}`}>
          {isUploading ? 'Enviando...' : asset ? 'Substituir' : 'Enviar arquivo'}
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            disabled={busy || !agentId}
            onChange={handleFileChange}
          />
        </label>

        {asset && agentId && (
          <button
            type="button"
            className="btn-secondary text-sm px-3 py-2"
            disabled={busy}
            onClick={handleRemove}
          >
            {isRemoving ? 'Removendo...' : 'Remover'}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function VisualOutputStyleFields({
  visualOutputStyleInstructions,
  setVisualOutputStyleInstructions,
  visualOutputAssets,
  setVisualOutputAssets,
  agentId,
  disabled = false,
}) {
  return (
    <div className="space-y-4 pl-1 border-l-2 border-surface-border ml-1 pt-2">
      <Textarea
        id="visual_output_style_instructions"
        label="Guia de estilo visual (opcional)"
        value={visualOutputStyleInstructions}
        onChange={(e) => setVisualOutputStyleInstructions(e.target.value)}
        placeholder="Ex.: paleta verde #00AA00, tipografia sans-serif, layout minimalista."
        hint="Texto livre aplicado a todas as gerações visuais deste agente."
        rows={4}
        disabled={disabled}
      />

      <VisualAssetUploadField
        label="Logo da marca"
        hint="PNG, JPG ou WebP (até 2 MB). Usada como referência na geração."
        assetType="logo"
        agentId={agentId}
        asset={visualOutputAssets?.logo}
        onAssetChange={(asset) =>
          setVisualOutputAssets((current) => {
            const next = { ...(current || {}) };
            if (asset) {
              next.logo = asset;
            } else {
              delete next.logo;
            }
            return next;
          })
        }
        disabled={disabled}
      />

      <VisualAssetUploadField
        label="Imagem de referência (estilo/layout)"
        hint="PNG, JPG ou WebP (até 5 MB). Define o mood visual das peças geradas."
        assetType="reference"
        agentId={agentId}
        asset={visualOutputAssets?.reference}
        onAssetChange={(asset) =>
          setVisualOutputAssets((current) => {
            const next = { ...(current || {}) };
            if (asset) {
              next.reference = asset;
            } else {
              delete next.reference;
            }
            return next;
          })
        }
        disabled={disabled}
      />
    </div>
  );
}

export default VisualOutputStyleFields;
